import { useEffect, useMemo, useState } from 'react'

import ReactPaginate from 'react-paginate'
import { Col, Form, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap'
import { toast } from 'react-toastify'

import {
  ArrowClockwise,
  CaretDown,
  CaretUp,
  PencilFill,
  PlusCircleFill,
  Search,
  TrashFill,
} from 'react-bootstrap-icons'

import Navbar from '../../../components/navbar/navbar'
import { CustomSpinner } from '../../../components/CustomSpinner'
import { IconButton } from '../../../components/IconButton'
import { CustomButton } from '../../../components/CustomButton'

import { sendErrorApiMessage } from '../../../utils/sendErrorApiMessage'

import { useOrderTable } from '../../../hooks/useOrderTable'

import { Ask } from '../../../components/Ask'
import MotivosService from '../../../services/database/MotivosService'
import { ModalMotivo } from './ModalMotivo'
import { CustomIntegerInput } from '../../../components/CustomDecimalInput'
import { useCommand } from '../../../hooks/useCommand'

export function Motivos() {
  const [motivos, setMotivos] = useState([])
  const [isMotivosLoading, setIsMotivosLoading] = useState(true)

  const [selectedCodigo, setSelectedCodigo] = useState('')
  const [search, setSearch] = useState({
    type: 'descricao',
    text: '',
  })

  const [isCreateMotivoLoading, setIsCreateMotivoLoading] = useState(false)
  const [isUpdateMotivoLoading, setIsUpdateMotivoLoading] = useState(false)
  const [isSearchMotivoLoading, setIsSearchMotivoLoading] = useState(false)
  const [isDeleteMotivoLoading, setIsDeleteMotivoLoading] = useState({})

  const [pageCount, setPageCount] = useState(0)
  const [itemOffset, setItemOffset] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const [isModalCreateMotivoOpen, setIsModalCreateMotivoOpen] = useState(false)
  const [isModalUpdateMotivoOpen, setIsModalUpdateMotivoOpen] = useState(false)

  const endOffset = itemOffset + itemsPerPage

  const {
    sortedArray: sortedMotivos,
    check,
    handleOrganize,
    orderType,
  } = useOrderTable(motivos, 'codigo', 'asc')

  const filteredMotivos = useMemo(() => {
    setPageCount(Math.ceil(sortedMotivos.length / itemsPerPage))

    return sortedMotivos.slice(itemOffset, endOffset)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endOffset, itemOffset, sortedMotivos, orderType.field, orderType.type])

  const handlePageClick = (event) => {
    const newOffset = (event.selected * itemsPerPage) % motivos.length

    setItemOffset(newOffset)
  }

  async function handleCreateMotivo({ descricao }) {
    try {
      setIsCreateMotivoLoading(true)

      await MotivosService.create({ descricao })

      toast.success('Motivo criado com sucesso!')
      loadMotivos()
      setIsModalCreateMotivoOpen(false)
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível criar o motivo!')
    } finally {
      setIsCreateMotivoLoading(false)
    }
  }

  async function handleSearchMotivo() {
    try {
      setIsSearchMotivoLoading(true)

      const data = await MotivosService.search(search)

      setMotivos(data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao buscar motivo!')
    } finally {
      setIsSearchMotivoLoading(false)
    }
  }

  async function handleUpdateMotivo(data) {
    const { codigo, descricao } = data

    try {
      setIsUpdateMotivoLoading(true)

      await MotivosService.update(codigo, { descricao })

      loadMotivos()
      toast.success(`Motivo atualizado com sucesso!`)
      setIsModalUpdateMotivoOpen(false)
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível atualizar o motivo!')
    } finally {
      setIsUpdateMotivoLoading(false)
    }
  }

  async function handleDeleteMotivo(codigo, index) {
    try {
      setIsDeleteMotivoLoading((prevState) => ({
        ...prevState,
        [index]: true,
      }))

      await MotivosService.delete(codigo)

      loadMotivos()
      toast.success('Motivo deletado com sucesso!')
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível deletar o motivo!')
    } finally {
      setIsDeleteMotivoLoading((prevState) => ({
        ...prevState,
        [index]: false,
      }))
    }
  }

  async function loadMotivos() {
    try {
      setIsMotivosLoading(true)

      const data = await MotivosService.getAll()

      setMotivos(data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao carregar os motivos!')
    } finally {
      setIsMotivosLoading(false)
    }
  }

  useCommand(() => {
    setSearch((prevState) => ({
      ...prevState,
      text: '',
    }))

    loadMotivos()
  })

  useEffect(() => {
    loadMotivos()
  }, [])

  return (
    <>
      <Navbar />

      <div className="mt-page">
        <h1>Motivos</h1>

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 10,
          }}
        >
          <section>
            <p>Motivos por página</p>
            <Form.Select
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value))
              }}
              style={{
                width: '200px',
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </section>

          <CustomButton
            title="Cadastrar"
            color="#27ae60"
            onClick={() => setIsModalCreateMotivoOpen(true)}
          >
            <PlusCircleFill />
          </CustomButton>

          <CustomButton
            title="Recarregar"
            color="#0070da"
            onClick={loadMotivos}
            isLoading={isMotivosLoading}
            isLoadingText="Recarregando..."
          >
            <ArrowClockwise />
          </CustomButton>
        </div>

        <Row className="mt-3">
          <Col>
            <Form.Select
              value={search.type}
              onChange={(e) =>
                setSearch((prevState) => ({
                  ...prevState,
                  type: e.target.value,
                }))
              }
            >
              <option value="codigo">Código</option>
              <option value="descricao">Descrição</option>
            </Form.Select>
          </Col>

          <Col>
            {search.type === 'codigo' ? (
              <CustomIntegerInput
                placeholder="Pesquise aqui..."
                value={search.text}
                onChange={(e) =>
                  setSearch((prevState) => ({
                    ...prevState,
                    text: e.target.value,
                  }))
                }
                onKeyDown={(e) => {
                  if (e.target.value.trim().length > 0 && e.key === 'Enter') {
                    handleSearchMotivo()
                  }
                }}
              />
            ) : (
              <Form.Control
                placeholder="Pesquise aqui..."
                value={search.text}
                onChange={(e) =>
                  setSearch((prevState) => ({
                    ...prevState,
                    text: e.target.value,
                  }))
                }
                onKeyDown={(e) => {
                  if (e.target.value.trim().length > 0 && e.key === 'Enter') {
                    handleSearchMotivo()
                  }
                }}
              />
            )}
          </Col>

          <Col>
            <IconButton
              isLoading={isSearchMotivoLoading}
              onClick={handleSearchMotivo}
              isDisabled={!search.text.trim()}
            >
              <Search />
            </IconButton>
          </Col>
        </Row>

        {isMotivosLoading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 50,
            }}
          >
            <CustomSpinner />
          </div>
        ) : filteredMotivos.length === 0 ? (
          <div
            style={{
              flex: 1,
              minHeight: '100%',
              padding: 50,
              textAlign: 'center',
            }}
          >
            <p>Sem motivos cadastrados!</p>
          </div>
        ) : (
          <>
            <Table
              striped
              bordered
              hover
              responsive
            >
              <thead>
                <tr>
                  <th onClick={() => handleOrganize('codigo')}>
                    <span>Código</span>
                    <CaretUp color={check('codigo', 'asc')} />
                    <CaretDown color={check('codigo', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('descricao')}>
                    <span>Descrição</span>
                    <CaretUp color={check('descricao', 'asc')} />
                    <CaretDown color={check('descricao', 'desc')} />
                  </th>

                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredMotivos.map((motivo, index) => (
                  <tr key={motivo.codigo}>
                    <td>{motivo.codigo}</td>
                    <td>{motivo.descricao}</td>
                    <td
                      style={{
                        display: 'flex',
                        gap: 5,
                      }}
                    >
                      <OverlayTrigger overlay={<Tooltip>Editar</Tooltip>}>
                        <IconButton
                          color="#e67e22"
                          onClick={() => {
                            setSelectedCodigo(motivo.codigo)
                            setIsModalUpdateMotivoOpen(true)
                          }}
                        >
                          <PencilFill />
                        </IconButton>
                      </OverlayTrigger>

                      <OverlayTrigger overlay={<Tooltip>Excluir</Tooltip>}>
                        <IconButton
                          isLoading={isDeleteMotivoLoading[index]}
                          color="#c0392b"
                          onClick={() => {
                            Ask('Tem certeza que deseja excluir?', () =>
                              handleDeleteMotivo(motivo.codigo, index),
                            )
                          }}
                        >
                          <TrashFill />
                        </IconButton>
                      </OverlayTrigger>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <ReactPaginate
              breakLabel="..."
              nextLabel="Próxima"
              onPageChange={handlePageClick}
              pageRangeDisplayed={5}
              pageCount={pageCount}
              previousLabel="Anterior"
              renderOnZeroPageCount={null}
              containerClassName="pagination"
              activeClassName="active"
            />
          </>
        )}
      </div>

      {isModalCreateMotivoOpen && (
        <ModalMotivo
          isOpen={isModalCreateMotivoOpen}
          onClose={() => setIsModalCreateMotivoOpen(false)}
          title="Criar Motivo"
          isRequestLoading={isCreateMotivoLoading}
          onRequest={handleCreateMotivo}
        />
      )}

      {isModalUpdateMotivoOpen && (
        <ModalMotivo
          codigo={selectedCodigo}
          isOpen={isModalUpdateMotivoOpen}
          onClose={() => setIsModalUpdateMotivoOpen(false)}
          title="Editar Motivo"
          isRequestLoading={isUpdateMotivoLoading}
          onRequest={handleUpdateMotivo}
        />
      )}
    </>
  )
}
